// Cliente HTTP centralizado para o backend do CookieBot.
import axios from "axios";

// REACT_APP_BACKEND_URL pode ser:
//   - URL absoluta:  https://backend.linux08.sbs   (cross-origin)
//   - Vazia/ausente: ""                            (MESMA origem do frontend → recomendado em produção com Nginx)
const BASE_URL = process.env.REACT_APP_BACKEND_URL || "";
export const API_BASE = `${BASE_URL}/api`;
const TOKEN_KEY = "cookiebot:token";

export const obterToken = () => localStorage.getItem(TOKEN_KEY);
export const salvarToken = (t) => localStorage.setItem(TOKEN_KEY, t);
export const limparToken = () => localStorage.removeItem(TOKEN_KEY);

export const api = axios.create({
    baseURL: API_BASE,
    timeout: 60000,
});

// Injeta Bearer token em todas as chamadas (se houver).
api.interceptors.request.use((config) => {
    const t = obterToken();
    if (t) config.headers.Authorization = `Bearer ${t}`;
    return config;
});

// Em 401, dispara evento para AuthContext reagir (logout + redirect).
api.interceptors.response.use(
    (r) => r,
    (err) => {
        if (err?.response?.status === 401) {
            limparToken();
            window.dispatchEvent(new CustomEvent("auth:nao-autorizado"));
        }
        return Promise.reject(err);
    }
);

export const construirUrlWS = (botId) => {
    // Quando BASE_URL é vazia (mesma origem), usa o host/proto da página.
    let host;
    let proto;
    if (BASE_URL) {
        host = BASE_URL.replace(/^https?:\/\//, "");
        proto = BASE_URL.startsWith("https") ? "wss" : "ws";
    } else {
        host = window.location.host;
        proto = window.location.protocol === "https:" ? "wss" : "ws";
    }
    const t = obterToken() || "";
    return `${proto}://${host}/api/ws/${botId}?token=${encodeURIComponent(t)}`;
};

// ---------- Auth ----------
export const login = async (email, senha) =>
    (await api.post("/auth/login", { email, senha })).data;
export const eu = async () => (await api.get("/auth/eu")).data;

// ---------- Bots ----------
export const listarBots = async () => (await api.get("/bots")).data;
export const criarBot = async (payload) => (await api.post("/bots", payload)).data;
export const obterBot = async (id) => (await api.get(`/bots/${id}`)).data;
export const atualizarBot = async (id, payload) => (await api.patch(`/bots/${id}`, payload)).data;
export const removerBot = async (id) => (await api.delete(`/bots/${id}`)).data;
export const clonarBot = async (id, nome) => (await api.post(`/bots/${id}/clonar`, { nome })).data;
export const iniciarBot = async (id) => (await api.post(`/bots/${id}/iniciar`)).data;
export const pararBot = async (id) => (await api.post(`/bots/${id}/parar`)).data;
export const reiniciarBot = async (id) => (await api.post(`/bots/${id}/reiniciar`)).data;
export const atualizarPagina = async (id) => (await api.post(`/bots/${id}/atualizar-pagina`)).data;
export const navegarParaURL = async (id, url) => (await api.post(`/bots/${id}/navegar`, { url })).data;
export const clicarNoNavegador = async (id, x, y) => (await api.post(`/bots/${id}/clicar`, { x, y })).data;
export const digitarNoNavegador = async (id, texto) => (await api.post(`/bots/${id}/digitar`, { texto })).data;
export const teclarNoNavegador = async (id, tecla) => (await api.post(`/bots/${id}/teclar`, { tecla })).data;
export const obterScreenshot = async (id) => (await api.get(`/bots/${id}/screenshot`)).data;

// ---------- Requisições / cookies / logs ----------
export const listarRequisicoes = async (id, limite = 200) =>
    (await api.get(`/bots/${id}/requisicoes`, { params: { limite } })).data;
export const limparRequisicoes = async (id) => (await api.delete(`/bots/${id}/requisicoes`)).data;
export const listarCookies = async (id) => (await api.get(`/bots/${id}/cookies`)).data;
// Injeta cookies no navegador. Aceita 3 formatos no payload:
//   { cookies_txt: "<conteúdo formato Netscape>", navegar?: true, dominio?: "google.com" }
//   { cookies: [{nome, valor, dominio, caminho, expira, http_only, secure, same_site}], navegar?: true }
//   { cookie_header: "a=1; b=2", dominio: "google.com", navegar?: true }
export const injetarCookies = async (id, payload) =>
    (await api.post(`/bots/${id}/cookies/injetar`, payload)).data;
export const listarLogs = async (id, limite = 200) =>
    (await api.get(`/bots/${id}/logs`, { params: { limite } })).data;

// ---------- Regras de encaminhamento ----------
export const listarRegras = async (botId) => (await api.get(`/bots/${botId}/regras`)).data;
export const criarRegra = async (botId, payload) => (await api.post(`/bots/${botId}/regras`, payload)).data;
export const atualizarRegra = async (idRegra, payload) =>
    (await api.patch(`/regras/${idRegra}`, payload)).data;
export const removerRegra = async (idRegra) => (await api.delete(`/regras/${idRegra}`)).data;
export const executarRegra = async (idRegra, requisicaoId = null) =>
    (await api.post(`/regras/${idRegra}/executar`, { requisicao_id: requisicaoId })).data;

// Auxiliares do editor de regras
export const listarVariaveisDisponiveis = async () =>
    (await api.get(`/regras/variaveis`)).data;
export const previewTemplateRegra = async (payload) =>
    (await api.post(`/regras/preview`, payload)).data;
